import { useState } from "react";
import { Upload, FileText, Loader2 } from "lucide-react";
import { parseDocxFileInBrowser } from "@/lib/docxParserBrowser";
import type { ParsedQuestionDocResult } from "@/types/questions";

interface Props {
  onParsed: (result: ParsedQuestionDocResult, file: File) => void;
}

export default function QuestionUploadPanel({ onParsed }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(file: File) {
    setBusy(true); setError(null);
    try {
      const result = await parseDocxFileInBrowser(file);
      onParsed(result, file);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not parse the document.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card-dark p-6">
      <div className="flex items-center gap-2 mb-3">
        <Upload className="w-5 h-5 text-forge-400" />
        <h2 className="font-display text-lg text-white">Upload .docx question bank</h2>
      </div>
      <p className="text-sm text-steel-300 mb-4">
        Drop a Word document. Supported: <code>Q1: …</code> with <code>A./B./C./D.</code> and{" "}
        <code>Correct: B</code> markers, plus the legacy plain-question format.
      </p>

      <label className="block border-2 border-dashed border-steel-700 rounded-2xl p-8 text-center cursor-pointer hover:border-forge-500 transition">
        <input
          type="file"
          accept=".docx"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handle(e.target.files[0])}
        />
        {busy ? (
          <div className="flex items-center justify-center gap-2 text-steel-200">
            <Loader2 className="w-4 h-4 animate-spin" /> Parsing…
          </div>
        ) : (
          <div className="text-steel-300">
            <FileText className="w-8 h-8 mx-auto mb-2 text-forge-400" />
            Click to choose a .docx file (or drop one here)
          </div>
        )}
      </label>

      {error && <div className="pill-bad mt-4 px-3 py-2">{error}</div>}
    </div>
  );
}
